import * as React from 'react';
import {useJsonForms, withJsonFormsControlProps} from '@jsonforms/react';
import FileUpload from './FileUpload';
import {SchemaUtils} from "../../util/SchemaUtils";
import {ControlValueFactory} from "../../factory/ControlValueFactory";
import {Component} from "../../enum/Component";
import { useMemo, useCallback, useState } from 'react';

interface FileUploadControlProps {
    data: any;
    handleChange(path: string, files: Array<any>): void;
    path: string;
    uischema: any;
    schema: any;
    rootSchema: any;
    appValidated?: boolean;
}

const FileUploadControl = ({ uischema, schema, handleChange, path, data, rootSchema, appValidated = false }: FileUploadControlProps) => {

    const [error, setError] = useState({code: -1, message: ""});

    const jsonForms = useJsonForms();

    const visible = useMemo(() =>
            SchemaUtils.isComponentVisible(uischema, jsonForms.core!.data),
        [uischema, jsonForms.core!.data]
    );

    const required = useMemo(() =>
            SchemaUtils.isRequired(path, rootSchema),
        [path, rootSchema]
    );

    const value = useMemo(() =>
            ControlValueFactory.getControlValue(Component.FileUpload, data, uischema.scope, jsonForms, visible),
        [Component.FileUpload, data, uischema.scope, jsonForms, visible]
    );

    const handleFileUploadChange = useCallback((files: Array<any>) => {
        let updatedFiles = [...(value || [])];
        let newError = {code: -1, message: ""};

        for(const file of files){
            // Create blob URL for files that don't have one (non-images)
            if (file instanceof Blob && (!(file as any).preview?.url)) {
                const blobUrl = URL.createObjectURL(file);

                // Directly mutate the preview property instead of cloning
                if (!(file as any).preview) {
                    (file as any).preview = {};
                }
                (file as any).preview.url = blobUrl;
            }

            if (updatedFiles.find(e => e.name === file.name)) {
                newError = {code: 1, message: `${file.name} has already been added.`};
                break;
            } else if(uischema.options?.maxFiles && updatedFiles.length >= uischema.options.maxFiles) {
                newError = {code: 1, message: `Maximum number of files limit has been reached.`};
                break;
            } else {
                updatedFiles.push(file);
            }
        }

        setError(newError);
        if (newError.code === -1) {
            handleChange(path, updatedFiles);
        }
    }, [value, uischema.options?.maxFiles, handleChange, path]);

    const revokePreviewUrl = useCallback((file: any) => {
        if (typeof file?.preview?.url === 'string' && file.preview.url.startsWith('blob:')) {
            URL.revokeObjectURL(file.preview.url);
        }
    }, []);

    // Memoize remove handlers
    const handleRemoveFile = useCallback((fileId: string) => {
        setError({code: -1, message: ""});
        const filteredFiles = (value || []).filter((file: any) => {
            if (file.id === fileId) {
                revokePreviewUrl(file);
            }
            return file.id !== fileId;
        });
        handleChange(path, filteredFiles);
    }, [value, handleChange, path, revokePreviewUrl]);

    const handleRemoveFiles = useCallback(() => {
        setError({code: -1, message: ""});
        (value || []).forEach(revokePreviewUrl);
        handleChange(path, []);
    }, [value, handleChange, path, revokePreviewUrl]);

    const handleClearError = useCallback(() => {
        setError({code: -1, message: ""});
    }, []);

    // Memoize error handler
    const handleFileUploadError = useCallback((error: any, file: any) => {
        let errorMessage = "";

        switch(error.code) {
            case 1: {
                errorMessage = `${file.extension} file uploads are not supported`;
                break;
            }
            case 2: {
                const maxSize = uischema.options?.maxFileSize || 0;
                const sizeInMB = (maxSize / (1024 * 1024)).toFixed(2);
                errorMessage = `${file.name} is too large and cannot be uploaded. The maximum file size supported is ${sizeInMB} MB`;
                break;
            }
            case 3: {
                errorMessage = `${file.name} is too small and cannot be uploaded`;
                break;
            }
            case 4: {
                const maxFiles = uischema.options?.maxFiles || 0;
                errorMessage = `The maximum number of files is ${maxFiles} that can be uploaded with this component`;
                break;
            }
            default: {
                errorMessage = error.message || "An error occurred during file upload";
            }
        }

        setError({code: error.code, message: errorMessage});
    }, [uischema.options?.maxFileSize, uischema.options?.maxFiles]);

    // Memoize FileUpload props
    const fileUploadProps = useMemo(() => ({
        error: error.message,
        required: required,
        label: uischema.label,
        description: schema.description || "",
        onChange: handleFileUploadChange,
        onError: handleFileUploadError,
        visible: visible,
        options: uischema.options || {},
        files: value || [],
        onRemoveAll: handleRemoveFiles,
        onRemoveOne: handleRemoveFile,
        onClearError: handleClearError,
        appValidated: appValidated
    }), [
        error.message,
        required,
        uischema.label,
        schema.description,
        handleFileUploadChange,
        handleFileUploadError,
        visible,
        uischema.options,
        value,
        handleRemoveFiles,
        handleRemoveFile,
        handleClearError,
        appValidated
    ]);

    return <FileUpload {...fileUploadProps} />;
};

export default React.memo(withJsonFormsControlProps(FileUploadControl));
